/**
 * Clip model for the teach-studio timeline: turns a reveal step's heterogeneous
 * arrays (elementIds + elementTimings, annotations, cameraKeyframes) into a flat
 * list of positioned "clips" the timeline can render/drag, and writes edited
 * clip windows back onto a step (full-step replace via upsertRevealStep).
 *
 * A clip's window is `[startMs, endMs)` against the step's narration. `openEnd`
 * means the thing has no explicit end — a reveal/annotation that persists past
 * the step, or a camera move that auto-returns — drawn as a full-width clip.
 */

import type { Annotation, CameraKeyframe, RevealStep, ScriptElement, Slide } from '@/lib/schema.types';

export type ClipKind = 'reveal' | 'annotation' | 'camera' | 'draw' | 'spotlight';

/** A stable reference to a single clip, used for selection across re-renders. */
export interface ClipRef {
  stepId: string;
  kind: ClipKind;
  id: string; // elementId | annotation.id | cameraKeyframe.id
}

export interface TimelineClip {
  kind: ClipKind;
  id: string;
  label: string;
  color: string;
  startMs: number;
  /** Resolved end, clamped to the step duration for display. */
  endMs: number;
  /** No explicit `endMs` was stored → the clip stays/auto-returns (full width). */
  openEnd: boolean;
}

/** New clip edit produced by a drag/resize, before it's written onto the step. */
export interface ClipWindow {
  startMs: number;
  endMs: number;
  openEnd: boolean;
  startWordIndex?: number;
  endWordIndex?: number;
}

export const FALLBACK_STEP_MS = 3000;
const EPS = 8;

export function clipRefEquals(a: ClipRef | null, b: ClipRef | null): boolean {
  return !!a && !!b && a.stepId === b.stepId && a.kind === b.kind && a.id === b.id;
}

/** Effective duration (ms) of a step — its audio length, else the words/keyframe
 *  extent, else a sensible default. This sets the timeline width of the step. */
export function stepDurationMs(step: RevealStep): number {
  const a = step.audio?.durationMs;
  if (a && a > 0) return a;
  const words = step.audio?.words;
  if (words && words.length) return Math.max(1, Math.round(words[words.length - 1].end * 1000));
  const kfs = step.cameraKeyframes ?? [];
  if (kfs.length) {
    const end = Math.max(...kfs.map((k) => (k.triggerMs ?? 0) + (k.transitionMs ?? 800)));
    return Math.max(FALLBACK_STEP_MS, end + 1200);
  }
  return FALLBACK_STEP_MS;
}

export function elementMapOf(slide: Slide): Map<string, ScriptElement> {
  const m = new Map<string, ScriptElement>();
  slide.rows.forEach((r) => r.columns.forEach((c) => c.elements.forEach((e) => m.set(e.id, e))));
  return m;
}

function resolveWindow(triggerMs: number | undefined, endMs: number | undefined, dur: number) {
  const startMs = triggerMs != null && triggerMs > 0 ? triggerMs : 0;
  const openEnd = endMs == null;
  const resolvedEnd = openEnd ? dur : Math.min(endMs as number, dur);
  return { startMs, endMs: Math.max(startMs + 1, resolvedEnd), openEnd };
}

const REVEAL_COLOR = '#6366f1';
const CAMERA_COLOR = '#0ea5e9';

/** The stored timing fields (if any) for one clip, regardless of its kind. */
function storedTiming(
  step: RevealStep,
  kind: ClipKind,
  id: string,
): { triggerMs?: number; endMs?: number; startWordIndex?: number; endWordIndex?: number } | undefined {
  if (kind === 'reveal') return step.elementTimings?.[id];
  if (kind === 'annotation') return step.annotations?.find((a) => a.id === id);
  return step.cameraKeyframes?.find((k) => k.id === id);
}

/** Resolve the current display window of a clip — used as the base for edits. */
export function currentWindow(
  step: RevealStep,
  kind: ClipKind,
  id: string,
  dur: number,
): { startMs: number; endMs: number; openEnd: boolean } {
  const t = storedTiming(step, kind, id);
  return resolveWindow(t?.triggerMs, t?.endMs, dur);
}

/** Which edges of a clip are word-bound (drives the 🔗 indicator). */
export function clipBinding(step: RevealStep, kind: ClipKind, id: string): { start: boolean; end: boolean } {
  const t = storedTiming(step, kind, id);
  return { start: t?.startWordIndex != null, end: t?.endWordIndex != null };
}

export function buildReveals(
  step: RevealStep,
  dur: number,
  elementMap: Map<string, ScriptElement>,
  labelFor: (el?: ScriptElement) => string,
): TimelineClip[] {
  const timings = step.elementTimings ?? {};
  return (step.elementIds ?? []).map((id) => {
    const t = timings[id];
    const w = resolveWindow(t?.triggerMs, t?.endMs, dur);
    return {
      kind: 'reveal' as const,
      id,
      label: labelFor(elementMap.get(id)),
      color: REVEAL_COLOR,
      ...w,
    };
  });
}

export function buildAnnotations(step: RevealStep, dur: number): TimelineClip[] {
  return (step.annotations ?? []).map((a: Annotation) => {
    const w = resolveWindow(a.triggerMs, a.endMs, dur);
    return {
      kind: 'annotation' as const,
      id: a.id,
      label: a.kind,
      color: a.color ?? '#facc15',
      ...w,
    };
  });
}

export function buildCameras(step: RevealStep, dur: number): TimelineClip[] {
  return (step.cameraKeyframes ?? []).map((k: CameraKeyframe) => {
    const w = resolveWindow(k.triggerMs, k.endMs, dur);
    const labelCount = k.labels?.length ?? 0;
    return {
      kind: 'camera' as const,
      id: k.id,
      label: labelCount > 0 ? `Camera · ${labelCount} label${labelCount === 1 ? '' : 's'}` : 'Camera move',
      color: CAMERA_COLOR,
      ...w,
    };
  });
}

/** Convert an edited display window into stored `{triggerMs, endMs}` + word hints:
 *  a 0 start and a full-width end collapse to "absent" so defaults/persistence hold. */
function storedFields(win: ClipWindow, dur: number) {
  const triggerMs = win.startMs > 0 ? Math.round(win.startMs) : undefined;
  const endMs = win.openEnd || win.endMs >= dur - EPS ? undefined : Math.round(win.endMs);
  return {
    triggerMs,
    endMs,
    startWordIndex: triggerMs != null ? win.startWordIndex : undefined,
    endWordIndex: endMs != null ? win.endWordIndex : undefined,
  };
}

/** Write an edited clip window back onto a copy of the step (for upsertRevealStep). */
export function applyClipWindow(
  step: RevealStep,
  kind: ClipKind,
  id: string,
  win: ClipWindow,
  dur: number,
): RevealStep {
  const f = storedFields(win, dur);
  if (kind === 'reveal') {
    const timings = { ...(step.elementTimings ?? {}) };
    timings[id] = {
      triggerMs: f.triggerMs,
      endMs: f.endMs,
      startWordIndex: f.startWordIndex,
      endWordIndex: f.endWordIndex,
    };
    return { ...step, elementTimings: timings };
  }
  if (kind === 'annotation') {
    return {
      ...step,
      annotations: (step.annotations ?? []).map((a) =>
        a.id === id
          ? { ...a, triggerMs: f.triggerMs, endMs: f.endMs, startWordIndex: f.startWordIndex, endWordIndex: f.endWordIndex }
          : a,
      ),
    };
  }
  return {
    ...step,
    cameraKeyframes: (step.cameraKeyframes ?? []).map((k) =>
      k.id === id
        ? { ...k, triggerMs: f.triggerMs, endMs: f.endMs, startWordIndex: f.startWordIndex, endWordIndex: f.endWordIndex }
        : k,
    ),
  };
}

/** Remove a clip (element reveal / annotation / camera keyframe) from a step. */
export function removeClip(step: RevealStep, kind: ClipKind, id: string): RevealStep {
  if (kind === 'reveal') {
    const timings = { ...(step.elementTimings ?? {}) };
    delete timings[id];
    return {
      ...step,
      elementIds: (step.elementIds ?? []).filter((e) => e !== id),
      elementTimings: timings,
    };
  }
  if (kind === 'annotation') {
    return { ...step, annotations: (step.annotations ?? []).filter((a) => a.id !== id) };
  }
  return { ...step, cameraKeyframes: (step.cameraKeyframes ?? []).filter((k) => k.id !== id) };
}
